import { Box } from '@material-ui/core';
import DataGrid, { Column, ColumnFixing, Grouping, Sorting } from 'devextreme-react/data-grid';
import { RowPreparedEvent } from 'devextreme/ui/data_grid';
import { DataGridRef } from 'hooks/useDataGridExporter';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const DataGridContainer = styled(Box)`
  && {
    .dx-datagrid {
      font-family: 'Poppins', sans-serif;
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;
    }

    .dx-datagrid-header-panel,
    .dx-datagrid-headers,
    .dc-datagrid-headers > div,
    .dx-datagrid-headers .dx-datagrid-table .dx-row > td,
    .dx-datagrid-borders .dx-datagrid-rowsview,
    .dx-datagrid-headers + .dx-datagrid-rowsview {
      border: none;
    }

    .dx-datagrid-headers {
      background-color: ${({ theme }) => theme.colors.lightsolitude};
      padding: 1rem 0.5rem;
      border-radius: 8px;
      font-weight: 500;
      color: ${({ theme }) => theme.colors.black};

      .dx-datagrid-group-space {
        background-color: ${({ theme }) => theme.colors.lightsolitude};
      }
    }

    .dx-header-row td[id$='-fixed'] {
      background-color: ${({ theme }) => theme.colors.lightsolitude};
      position: relative;
      left: 0.5rem;
    }

    .dx-datagrid-rowsview {
      padding: 1rem 0 0;
    }

    .dx-data-row td {
      padding: 1rem;
      border: none !important;
    }

    .dx-scrollable-content {
      padding-bottom: 1rem;
    }

    .dx-datagrid-content-fixed {
      padding-top: 1rem;
    }

    .dx-group-row {
      background-color: ${({ theme }) => theme.colors.lightsolitude} !important;

      td {
        padding: 1rem 0 !important;
      }

      &:first-child td {
        border-top: 1px solid #ddd;
      }
    }

    .popper-column {
      overflow: initial;
    }

    .action-column > div {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    tr:not(.dx-header-row) .checkbox-column > div {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    .dx-datagrid-rowsview .dx-row-focused.dx-data-row > td:not(.dx-focused) {
      background-color: #beddf1;
      border-bottom: 1px solid #beddf1 !important;
      color: initial;
    }

    .vertical-align-center .dx-datagrid-content .dx-datagrid-table .dx-row > td {
      vertical-align: middle !important;
    }

    .group-cells-with-buttons {
      .dx-datagrid-group-closed::before,
      .dx-datagrid-group-opened::before {
        top: 5px;
      }
    }
  }
`;

export const TextWithPopperCell = styled.div`
  height: 100%;
  display: flex;
  align-items: center;

  span {
    color: ${({ theme }) => theme.colors.black};
  }
`;

interface Props {
  tableRef: DataGridRef;
  children: ReactNode;
  dataSource: unknown[];
  rowKeyProperty: string;
  showRowNumbers: boolean;
  allowGrouping?: boolean;
  groupingAutoExpandAll?: boolean;
  wordWrapEnabled?: boolean;
  focusedRowEnabled?: boolean;
  rowContentsVerticalAlignCenter?: boolean;
  groupCellsWithButtons?: boolean;
  noDataText?: string;
  onRowPrepared?: (e: RowPreparedEvent<unknown, string>) => void;
}

const DcDataGrid = ({
  tableRef,
  dataSource,
  rowKeyProperty,
  children,
  showRowNumbers = false,
  allowGrouping = false,
  groupingAutoExpandAll = true,
  wordWrapEnabled = false,
  focusedRowEnabled = false,
  rowContentsVerticalAlignCenter = false,
  groupCellsWithButtons = false,
  noDataText,
  onRowPrepared,
}: Props) => {
  const { t } = useTranslation();
  return (
    <DataGridContainer className="dx-viewport">
      <DataGrid
        dataSource={dataSource}
        keyExpr={rowKeyProperty}
        showBorders={false}
        showColumnLines={false}
        columnAutoWidth={true}
        paging={{ enabled: false }}
        wordWrapEnabled={wordWrapEnabled}
        focusedRowEnabled={focusedRowEnabled}
        noDataText={t(noDataText ?? 'lists_noData')}
        ref={tableRef}
        allowColumnResizing
        allowColumnReordering
        rowAlternationEnabled
        className={[
          rowContentsVerticalAlignCenter ? 'vertical-align-center' : '',
          groupCellsWithButtons ? 'group-cells-with-buttons' : '',
        ].join(' ')}
        onRowPrepared={onRowPrepared}
      >
        <Sorting mode="multiple" />

        {allowGrouping ? <Grouping autoExpandAll={groupingAutoExpandAll} /> : null}

        {showRowNumbers ? (
          <Column
            caption="#"
            allowSorting={false}
            cellTemplate={(
              cellElement: HTMLTableCellElement,
              cellInfo: { row: { rowIndex: number } },
            ) => {
              cellElement.innerText = (cellInfo.row.rowIndex + 1).toString();
            }}
          />
        ) : null}

        {children}
        <ColumnFixing enabled={true} />
      </DataGrid>
    </DataGridContainer>
  );
};

export default DcDataGrid;
